import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { ANIMATION_TIMEOUT_MS } from "../defaults";

/**
 * Tracks whether a shell panel animation is in progress.
 *
 * Fires when `value` changes (skips the initial mount), provides a timeout
 * fallback to stop after ANIMATION_TIMEOUT_MS, and exposes `endAnimation` so
 * components can stop it early via onTransitionEnd.
 */
export function useAnimTick<T>(value: T) {
  const [animTick, setAnimTick] = useState(0);
  const [previousValue, setPreviousValue] = useState(value);
  const prevRef = useRef(value);
  const hasValueChanged = prevRef.current !== value;
  const transitionFrom = hasValueChanged ? prevRef.current : previousValue;

  useLayoutEffect(() => {
    if (prevRef.current !== value) {
      setPreviousValue(prevRef.current);
      prevRef.current = value;
      setAnimTick(n => n + 1);
    }
  }, [value]);

  useEffect(() => {
    if (animTick === 0) return;
    const id = setTimeout(() => setAnimTick(0), ANIMATION_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [animTick]);

  return {
    isAnimating: hasValueChanged || animTick > 0,
    previousValue: transitionFrom,
    endAnimation: () => setAnimTick(0),
  };
}
